/**
 * TypeScript mirrors of the Bulk Import module DTOs (G7.2 / G7.3).
 *
 * Source of truth — field names taken VERBATIM from
 * backend/internal/handlers/bulk_import.go (bulkImportDTO,
 * previewResponse, commitBulkImportRequest, commitResponse,
 * commitRowDTO) and backend/internal/handlers/bulk_import_leave_training.go
 * (leaveBalanceCommitRowDTO, trainingAssignmentCommitRowDTO). The
 * bulkimport.RowOutcome / RowError shapes come from
 * backend/internal/services/bulkimport/csv.go.
 *
 * Per-row outcomes: the preview returns flat RowOutcomes (no resource
 * ids — nothing's been written yet); the commit response carries a
 * richer per-row DTO that includes the created user_id / employee_id
 * for the OK rows. We model both because the wizard reads both.
 */

// ---------------------------------------------------------------------------
// Import type enum — mirrors bulk_imports.import_type CHECK constraint
// in migration 026. The URL-path segments use kebab-case so the wire
// pattern is `/bulk-imports/{kebab}/preview|commit`; the DB literal is
// snake_case. We model the DB literal here so the history list filter
// matches the column value.
// ---------------------------------------------------------------------------

export const BULK_IMPORT_TYPES = [
  "employees",
  "leave_balances",
  "training_assignments",
  "assets",
  "opening_ytd",
] as const;

export type BulkImportType = (typeof BULK_IMPORT_TYPES)[number];

/** Kebab-case URL segments per type (matches router.go). */
export const BULK_IMPORT_URL_SEGMENT: Record<BulkImportType, string> = {
  employees: "employees",
  leave_balances: "leave-balances",
  training_assignments: "training-assignments",
  assets: "assets",
  opening_ytd: "opening-ytd",
};

/** Human label per type. */
export const BULK_IMPORT_LABELS: Record<BulkImportType, string> = {
  employees: "Employees",
  leave_balances: "Leave-Balance Grants",
  training_assignments: "Training Assignments",
  assets: "Assets",
  opening_ytd: "Salary Paid Before WorkRight",
};

/** Short noun used in success banners ("Imported N employees"). */
export const BULK_IMPORT_NOUN_PLURAL: Record<BulkImportType, string> = {
  employees: "employees",
  leave_balances: "leave balances",
  training_assignments: "training assignments",
  assets: "assets",
  opening_ytd: "salary records",
};

/** Singular of BULK_IMPORT_NOUN_PLURAL ("Commit import (1 employees)"). */
export const BULK_IMPORT_NOUN_SINGULAR: Record<BulkImportType, string> = {
  employees: "employee",
  leave_balances: "leave balance",
  training_assignments: "training assignment",
  assets: "asset",
  opening_ytd: "salary record",
};

/**
 * Verb in the success banner — different per type to keep the copy
 * accurate ("imported" vs "granted" vs "assigned").
 */
export const BULK_IMPORT_SUCCESS_VERB: Record<BulkImportType, string> = {
  employees: "Imported",
  leave_balances: "Granted",
  training_assignments: "Assigned",
  assets: "Imported",
  opening_ytd: "Saved",
};

// ---------------------------------------------------------------------------
// Per-row outcome shapes (mirror bulkimport.RowOutcome / RowError)
// ---------------------------------------------------------------------------

export interface BulkImportRowError {
  row_number: number;
  field?: string;
  code: string;
  message: string;
}

/** Preview-time per-row outcome — no resource id yet (nothing committed). */
export interface BulkImportRowOutcome {
  row_number: number;
  status: "ok" | "error";
  errors?: BulkImportRowError[];
  /** Opening-YTD preview only: who the row matched (by employee code,
   *  then work email — never the internal id), so HR can verify. */
  employee_name?: string;
  employee_code?: string;
  /** Employees preview: the row's monthly salary from the file (paise). */
  salary_basic_paise?: number;
  salary_hra_paise?: number;
  salary_special_allowance_paise?: number;
  /** Employees preview: non-blocking notices — the row still imports
   *  (e.g. `no_salary`: they'll need one before payroll). */
  warnings?: BulkImportRowError[];
}

/** Commit-time employee row outcome — adds user_id on OK rows. */
export interface EmployeeCommitRowOutcome {
  row_number: number;
  status: "ok" | "error";
  user_id?: number;
  errors?: BulkImportRowError[];
  /** Post-create follow-ups that could not be applied on an imported row
   *  (manager link, onboarding). */
  warnings?: BulkImportRowError[];
}

/** Onboarding outcome on the employees commit response (when requested). */
export interface BulkOnboardingOutcome {
  requested: boolean;
  status: string;
  template_id?: number;
  assigned: number;
  failed: number;
  message: string;
}

/** Commit-time leave-balance row outcome — adds employee_id on OK rows. */
export interface LeaveBalanceCommitRowOutcome {
  row_number: number;
  status: "ok" | "error";
  employee_id?: number;
  errors?: BulkImportRowError[];
}

/** Commit-time training-assignment row outcome — adds employee_id on OK rows. */
export interface TrainingAssignmentCommitRowOutcome {
  row_number: number;
  status: "ok" | "error";
  employee_id?: number;
  errors?: BulkImportRowError[];
}

/** Commit-time asset row outcome — adds asset_id on OK rows (A4). */
export interface AssetCommitRowOutcome {
  row_number: number;
  status: "ok" | "error";
  asset_id?: number;
  errors?: BulkImportRowError[];
}

/**
 * Union of every commit-time row outcome shape. The wizard's success
 * step renders them uniformly (row_number + status + errors) and only
 * dips into the resource id when the type matches.
 */
export type AnyCommitRowOutcome =
  | EmployeeCommitRowOutcome
  | LeaveBalanceCommitRowOutcome
  | TrainingAssignmentCommitRowOutcome
  | AssetCommitRowOutcome;

// ---------------------------------------------------------------------------
// Wire responses
// ---------------------------------------------------------------------------

/** Persisted bulk_imports row — mirrors handlers.bulkImportDTO. */
export interface BulkImport {
  id: number;
  tenant_id: number;
  initiated_by_id: number;
  import_type: BulkImportType;
  file_name: string;
  row_count: number;
  success_count: number;
  error_count: number;
  /** Raw error_log_jsonb — opaque JSON the detail page pretty-prints. */
  error_log: unknown;
  /** RFC3339. */
  initiated_at: string;
  /** RFC3339. */
  completed_at?: string;
}

/** Response from POST /bulk-imports/{type}/preview. */
export interface BulkImportPreviewResponse {
  file_name: string;
  file_hash: string;
  /** RFC3339 — 1-hour TTL on the staged blob. */
  expires_at: string;
  row_count: number;
  success_count: number;
  error_count: number;
  row_outcomes: BulkImportRowOutcome[];
  /** Employees preview only: the headcount meter at preview time. */
  seats?: {
    gated: boolean;
    limit?: number;
    used: number;
    available: number;
    over_limit: boolean;
  };
  /** Employees preview only: OK row numbers (file order) that exceed the
   *  seats available. Non-empty ⇒ commit would be refused (402), nothing
   *  imported — the import is all-or-nothing against the cap. */
  over_limit_rows?: number[];
  /** Employees preview only: rows of an OLD template (one that still has a
   *  password column) whose password cell was filled. Those passwords are
   *  ignored — everyone imported gets an email invite to set their own. */
  password_rows?: number;
  /** Whole-file, non-blocking notices (the rows still import) —
   *  `password_column_ignored` for an old employees template,
   *  `columns_not_used` for header cells the import did not use (HR1). */
  warnings?: BulkImportFileWarning[];
  /** column → the file's own header text, for columns matched by another
   *  name ("hire_date" ← "Date of Joining"). Absent when none were. */
  column_sources?: Record<string, string>;
  /** Employees preview only: departments in the file that aren't on the
   *  Departments list yet — the import adds them (never an error). */
  new_departments?: string[];
}

/** A whole-file, non-blocking preview notice (bulkimport.FileWarning). */
export interface BulkImportFileWarning {
  code: string;
  message: string;
  /** columns_not_used: the file's header cells the import did not use. */
  columns?: string[];
}

/** Response from POST /bulk-imports/employees/commit. */
export interface EmployeeCommitResponse {
  import: BulkImport;
  row_outcomes: EmployeeCommitRowOutcome[];
  onboarding?: BulkOnboardingOutcome;
  /** Departments the import added to the Departments list. */
  new_departments?: string[];
}

/** Response from POST /bulk-imports/leave-balances/commit. */
export interface LeaveBalanceCommitResponse {
  import: BulkImport;
  row_outcomes: LeaveBalanceCommitRowOutcome[];
}

/** Response from POST /bulk-imports/training-assignments/commit. */
export interface TrainingAssignmentCommitResponse {
  import: BulkImport;
  row_outcomes: TrainingAssignmentCommitRowOutcome[];
}

/** Response from POST /bulk-imports/opening-ytd/commit. */
export interface OpeningYTDCommitResponse {
  import: BulkImport;
  row_outcomes: { row_number: number; status: "ok" | "error"; employee_id?: number; errors?: BulkImportRowError[] }[];
}

/** Response from POST /bulk-imports/assets/commit (A4). */
export interface AssetCommitResponse {
  import: BulkImport;
  row_outcomes: AssetCommitRowOutcome[];
}

/** Discriminated commit response — the wizard cases on import_type. */
export type AnyCommitResponse =
  | EmployeeCommitResponse
  | LeaveBalanceCommitResponse
  | TrainingAssignmentCommitResponse
  | AssetCommitResponse
  | OpeningYTDCommitResponse;

/** GET /bulk-imports — list envelope. */
export interface ListBulkImportsResponse {
  items: BulkImport[];
}

/** POST /bulk-imports/{type}/commit — body shape. */
export interface CommitBulkImportRequest {
  file_hash: string;
  file_name?: string;
  /** Employees import only: assign the default onboarding template. Send
   *  `false` to opt out — when ABSENT the server assigns it whenever the
   *  company has an active template. */
  assign_onboarding?: boolean;
  onboarding_template_id?: number;
}

// ---------------------------------------------------------------------------
// CSV template column lists — mirror the backend bulkimport constants
// so the front-end "Download template" link emits the exact header the
// backend will accept. ANY DRIFT = ErrInvalidHeader on the first preview.
// ---------------------------------------------------------------------------

/** 36-column employee template — mirrors bulkimport.ExpectedEmployeeColumns.
 *  There is no password column: everyone imported gets an email invite to
 *  set their own password. (Old templates with a password column still
 *  import for now — the column is ignored and the preview says so.) */
export const EMPLOYEE_TEMPLATE_COLUMNS = [
  "email",
  "first_name",
  "last_name",
  "phone",
  "employee_id",
  "department",
  "designation",
  "role",
  "hire_date",
  "employment_type",
  "location",
  "cost_center",
  // Monthly amounts in RUPEES (25000, 25,000 or 2,50,000 — up to 2
  // decimals). A file with the old *_paise columns is refused with a
  // "download the new template" message.
  "salary_basic_inr",
  "salary_hra_inr",
  "salary_special_allowance_inr",
  "work_start_date",
  "pf_applicable",
  "bank_account_number",
  "bank_ifsc",
  "pan",
  "tax_regime",
  "date_of_birth",
  "emergency_contact_name",
  "emergency_contact_phone",
  "emergency_contact_relation",
  "nominee_name",
  "nominee_relation",
  "nominee_dob",
  "address_current",
  "address_permanent",
  // Wave 2: reporting line (manager's email — may be a row later in the same
  // file) + gender (male / female / other / prefer_not_to_say).
  "manager_email",
  "gender",
  // 2026-10: the date the salary columns took effect (YYYY-MM-DD). The
  // salary is recorded as the employee's opening salary revision from that
  // date (salary history) instead of from the joining date. Blank = joining
  // date, as before. The backend still accepts the header without this
  // column, and the older 31- / 33-column templates with a password column.
  "salary_effective_date",
  // 2026-10: the PF UAN (12 digits) and the ESIC IP number (10 digits) —
  // the ECR and the ESIC monthly file refuse members without them. The
  // backend still accepts the 33-column header without these two columns
  // (and every older template).
  "pf_uan",
  "esi_number",
  // 2026-10-30 (migration 167): `punching` — "not_required" = the person
  // doesn't need to punch; "required" or blank = the company's Punching
  // setting. Appended last, so older templates stay a strict prefix.
  "punching",
] as const;

/** 4-column leave-balance template — mirrors bulkimport.ExpectedLeaveBalanceColumns. */
export const LEAVE_BALANCE_TEMPLATE_COLUMNS = [
  "employee_id_or_email",
  "leave_type_code",
  "grant_days",
  "reason",
] as const;

/** 3-column training-assignment template — mirrors bulkimport.ExpectedTrainingAssignmentColumns. */
export const TRAINING_ASSIGNMENT_TEMPLATE_COLUMNS = [
  "employee_id_or_email",
  "training_module_code",
  "due_date",
] as const;

/** 6-column asset template — mirrors bulkimport.ExpectedAssetColumns (A4). */
export const ASSET_TEMPLATE_COLUMNS = [
  "asset_type",
  "asset_tag",
  "serial_number",
  "purchase_price_inr", // rupees, e.g. 45,000
  "notes",
  "assigned_to_email",
] as const;

/** 9-column "salary paid before WorkRight" template — mirrors
 *  bulkimport.ExpectedOpeningYTDColumns (persona round 2026-10). Amounts in
 *  rupees; financial_year like 2026-27; covers_up_to YYYY-MM. Column 1 is
 *  the employee code or work email (the server also still accepts the
 *  first template's "employee_id_or_email" header). */
export const OPENING_YTD_TEMPLATE_COLUMNS = [
  // The HR employee code (as on the employee profile) or work email.
  "employee_code_or_email",
  "financial_year",
  "covers_up_to",
  "gross_salary_inr",
  "basic_inr",
  "hra_inr",
  "pf_inr",
  "professional_tax_inr",
  "tds_inr",
] as const;

export const BULK_IMPORT_TEMPLATE_COLUMNS: Record<
  BulkImportType,
  readonly string[]
> = {
  employees: EMPLOYEE_TEMPLATE_COLUMNS,
  leave_balances: LEAVE_BALANCE_TEMPLATE_COLUMNS,
  training_assignments: TRAINING_ASSIGNMENT_TEMPLATE_COLUMNS,
  assets: ASSET_TEMPLATE_COLUMNS,
  opening_ytd: OPENING_YTD_TEMPLATE_COLUMNS,
};

/** Default template filename per type. */
export const BULK_IMPORT_TEMPLATE_FILENAME: Record<BulkImportType, string> = {
  employees: "employees-template.csv",
  leave_balances: "leave-balances-template.csv",
  training_assignments: "training-assignments-template.csv",
  assets: "assets-template.csv",
  opening_ytd: "salary-paid-before-workright-template.csv",
};

// ---------------------------------------------------------------------------
// 5 MiB upload cap — mirrors bulkimport.MaxBytesPerImport. Surfaced on
// the upload step's helper text so HR doesn't waste a round-trip on
// oversized files.
// ---------------------------------------------------------------------------

export const BULK_IMPORT_MAX_BYTES = 5 * 1024 * 1024;
